import {
  FIXED_OUTCOME_ORDER,
  FIXED_SEVERITY_ORDER,
  type InsightResult,
  type OutcomeCategory,
  type Severity,
} from '../models/insight.types'
import type { SummaryInsightItem } from '../models/summaryInsight.types'

import type { FontAwesomeWeight, IconName, UiTone } from '@/shared/ui'

export type PresentedOutcomeCategory = OutcomeCategory | 'other'

export interface OutcomePresentation {
  key: PresentedOutcomeCategory
  labelKey: string
  descriptionKey: string
  metricLabelKey: string
  tone: UiTone
  icon: IconName
  iconWeight: FontAwesomeWeight
}

export interface SeverityPresentation {
  key: Severity
  labelKey: string
  tone: UiTone
  icon: IconName
  order: number
}

export interface SeverityBreakdownItem extends SeverityPresentation {
  count: number
}

export interface InsightSeverityBucket extends SeverityPresentation {
  items: InsightResult[]
}

export interface InsightOutcomeGroup extends OutcomePresentation {
  items: InsightResult[]
  buckets: InsightSeverityBucket[]
  totalImpact: number
}

export type InsightHistoryGroupBy = 'outcome' | 'severity'

export interface InsightHistoryBucket {
  key: PresentedOutcomeCategory | Severity
  labelKey: string
  tone: UiTone
  items: InsightResult[]
}

export interface InsightHistoryGroup extends InsightHistoryBucket {
  buckets: InsightHistoryBucket[]
  totalImpact: number
}

const OUTCOME_PRESENTATION: Readonly<Record<OutcomeCategory, OutcomePresentation>> = {
  revenue_gain: {
    key: 'revenue_gain',
    labelKey: 'insights.outcome.revenueGain',
    descriptionKey: 'insights.outcomeDescription.revenueGain',
    metricLabelKey: 'insights.impact.revenue',
    tone: 'info',
    icon: 'gem',
    iconWeight: 'duotone',
  },
  roas_uplift: {
    key: 'roas_uplift',
    labelKey: 'insights.outcome.roasUplift',
    descriptionKey: 'insights.outcomeDescription.roasUplift',
    metricLabelKey: 'insights.impact.roas',
    tone: 'info',
    icon: 'arrow-trend-up',
    iconWeight: 'solid',
  },
  cost_savings: {
    key: 'cost_savings',
    labelKey: 'insights.outcome.costSavings',
    descriptionKey: 'insights.outcomeDescription.costSavings',
    metricLabelKey: 'insights.impact.savings',
    tone: 'success',
    icon: 'coins',
    iconWeight: 'duotone',
  },
  lost_revenue: {
    key: 'lost_revenue',
    labelKey: 'insights.outcome.lostRevenue',
    descriptionKey: 'insights.outcomeDescription.lostRevenue',
    metricLabelKey: 'insights.impact.atRisk',
    tone: 'warning',
    icon: 'alert-sensor',
    iconWeight: 'duotone',
  },
  planning: {
    key: 'planning',
    labelKey: 'insights.outcome.planning',
    descriptionKey: 'insights.outcomeDescription.planning',
    metricLabelKey: 'insights.impact.other',
    tone: 'warning',
    icon: 'calendar-check',
    iconWeight: 'regular',
  },
}

const UNKNOWN_OUTCOME: OutcomePresentation = {
  key: 'other',
  labelKey: 'insights.outcome.other',
  descriptionKey: 'insights.outcomeDescription.other',
  metricLabelKey: 'insights.impact.other',
  // `success`, not `neutral`: v3's fallback outcome is GREEN, not grey — its impact card and badge
  // both fall through to `o-green-50`/`o-green-80` (`InsightDetailModal.vue:420,432,469`), which is
  // exactly this tone's `--ui-color-success-soft`. Grey read as "no outcome" on a card that has one.
  tone: 'success',
  icon: 'sparkle',
  iconWeight: 'solid',
}

const SEVERITY_PRESENTATION: Readonly<Record<Severity, Omit<SeverityPresentation, 'order'>>> = {
  needs_attention: {
    key: 'needs_attention',
    labelKey: 'insights.severity.needsAttention',
    tone: 'danger',
    icon: 'alert-triangle',
  },
  opportunity: {
    key: 'opportunity',
    labelKey: 'insights.severity.opportunity',
    tone: 'success',
    icon: 'chart',
  },
  informational: {
    key: 'informational',
    labelKey: 'insights.severity.informational',
    tone: 'info',
    icon: 'info',
  },
  actual: {
    key: 'actual',
    labelKey: 'insights.severity.actual',
    tone: 'neutral',
    icon: 'check',
  },
  other: {
    key: 'other',
    labelKey: 'insights.severity.other',
    tone: 'neutral',
    icon: 'circle-question',
  },
}

const THREE_DAYS_MS = 3 * 24 * 60 * 60 * 1_000

function normalizeKey(value: unknown): string {
  return typeof value === 'string'
    ? value
        .trim()
        .toLowerCase()
        .replace(/[\s-]+/g, '_')
    : ''
}

/**
 * Backend insight copy may be a ready sentence or an identifier-shaped fallback. Keep authored
 * prose untouched, but never leak enum/i18n-style tokens such as `fatigue_cost_at_risk` into UI.
 */
export function presentInsightTitle(value: unknown): string {
  if (typeof value !== 'string') return ''
  const title = value.trim()
  if (!title) return ''
  if (!/^[\p{L}\p{N}]+(?:[_-][\p{L}\p{N}]+)+$/u.test(title)) return title

  const locale = /[çğıöşüİıÇĞÖŞÜ]/u.test(title) ? 'tr-TR' : 'en-US'
  return title
    .split(/[_-]+/u)
    .filter(Boolean)
    .map((word) => {
      const lower = word.toLocaleLowerCase(locale)
      return lower.charAt(0).toLocaleUpperCase(locale) + lower.slice(1)
    })
    .join(' ')
}

export function normalizeOutcomeCategory(value: unknown): PresentedOutcomeCategory {
  const normalized = normalizeKey(value)
  if (FIXED_OUTCOME_ORDER.includes(normalized as OutcomeCategory)) {
    return normalized as OutcomeCategory
  }
  if (normalized.includes('lost') && normalized.includes('revenue')) return 'lost_revenue'
  if (normalized.includes('cost')) return 'cost_savings'
  if (normalized.includes('roas')) return 'roas_uplift'
  if (normalized.includes('revenue')) return 'revenue_gain'
  return 'other'
}

export function normalizeSeverity(value: unknown): Severity {
  const normalized = normalizeKey(value)
  return FIXED_SEVERITY_ORDER.includes(normalized as Severity) ? (normalized as Severity) : 'other'
}

const CONFIDENCE_KEYS: Readonly<Record<string, string>> = {
  actual: 'insights.confidence.actual',
  estimated: 'insights.confidence.estimated',
  projected: 'insights.confidence.projected',
}

export function confidenceLabel(value: unknown, t: (key: string) => string): string {
  const raw = String(value ?? '').trim()
  if (!raw) return ''
  const key = CONFIDENCE_KEYS[raw.toLowerCase()]
  if (key) return t(key)
  return raw
    .split(/[_\s]+/u)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

export function outcomePresentation(value: unknown): OutcomePresentation {
  const normalized = normalizeOutcomeCategory(value)
  return normalized === 'other' ? UNKNOWN_OUTCOME : OUTCOME_PRESENTATION[normalized]
}

export function severityPresentation(value: unknown): SeverityPresentation {
  const normalized = normalizeSeverity(value)
  return {
    ...SEVERITY_PRESENTATION[normalized],
    order: FIXED_SEVERITY_ORDER.indexOf(normalized),
  }
}

/** Contract 15/B25: a result remains new through its first three days. */
export function isNewInsight(createdAt: string | undefined, now = Date.now()): boolean {
  if (!createdAt) return false
  const timestamp = Date.parse(createdAt)
  if (!Number.isFinite(timestamp)) return false
  const age = now - timestamp
  return age >= 0 && age <= THREE_DAYS_MS
}

export function isNewResult(result: Pick<InsightResult, 'created_at'>, now = Date.now()): boolean {
  return isNewInsight(result.created_at, now)
}

export function sortInsightsForDisplay(
  left: Pick<InsightResult, 'impact_score' | 'created_at'>,
  right: Pick<InsightResult, 'impact_score' | 'created_at'>,
): number {
  const impactDelta = Number(right.impact_score ?? 0) - Number(left.impact_score ?? 0)
  if (impactDelta !== 0) return impactDelta
  return Date.parse(right.created_at ?? '') - Date.parse(left.created_at ?? '')
}

export function summarizeSeverityBreakdown(
  items: readonly Pick<InsightResult, 'severity'>[],
): SeverityBreakdownItem[] {
  const counts = new Map<Severity, number>()
  for (const item of items) {
    const severity = normalizeSeverity(item.severity)
    counts.set(severity, (counts.get(severity) ?? 0) + 1)
  }
  return FIXED_SEVERITY_ORDER.filter((key) => counts.has(key)).map((key) => ({
    ...severityPresentation(key),
    count: counts.get(key) ?? 0,
  }))
}

export function groupInsightsByOutcome(items: readonly InsightResult[]): InsightOutcomeGroup[] {
  const groups = new Map<PresentedOutcomeCategory, InsightResult[]>()
  for (const item of items) {
    const key = normalizeOutcomeCategory(item.outcome_category)
    const group = groups.get(key)
    if (group) group.push(item)
    else groups.set(key, [item])
  }
  const outcomeOrder: readonly PresentedOutcomeCategory[] = [...FIXED_OUTCOME_ORDER, 'other']
  return outcomeOrder.flatMap((key) => {
    const groupItems = groups.get(key)
    if (!groupItems?.length) return []
    const buckets = summarizeSeverityBreakdown(groupItems).map((presentation) => ({
      ...presentation,
      items: groupItems
        .filter((item) => normalizeSeverity(item.severity) === presentation.key)
        .sort(sortInsightsForDisplay),
    }))
    return [
      {
        ...outcomePresentation(key),
        items: [...groupItems].sort(sortInsightsForDisplay),
        buckets,
        totalImpact: groupItems.reduce((sum, item) => sum + Number(item.impact_score ?? 0), 0),
      },
    ]
  })
}

export function groupInsightsBySeverity(items: readonly InsightResult[]): InsightHistoryGroup[] {
  return FIXED_SEVERITY_ORDER.flatMap((severity) => {
    const groupItems = items.filter((item) => normalizeSeverity(item.severity) === severity)
    if (groupItems.length === 0) return []
    const outcomeOrder: readonly PresentedOutcomeCategory[] = [...FIXED_OUTCOME_ORDER, 'other']
    const buckets = outcomeOrder.flatMap((outcome) => {
      const bucketItems = groupItems
        .filter((item) => normalizeOutcomeCategory(item.outcome_category) === outcome)
        .sort(sortInsightsForDisplay)
      return bucketItems.length === 0
        ? []
        : [{ ...outcomePresentation(outcome), items: bucketItems }]
    })
    return [
      {
        ...severityPresentation(severity),
        items: [...groupItems].sort(sortInsightsForDisplay),
        buckets,
        totalImpact: groupItems.reduce((sum, item) => sum + Number(item.impact_score ?? 0), 0),
      },
    ]
  })
}

export function groupInsightsForHistory(
  items: readonly InsightResult[],
  groupBy: InsightHistoryGroupBy,
): InsightHistoryGroup[] {
  return groupBy === 'severity' ? groupInsightsBySeverity(items) : groupInsightsByOutcome(items)
}

/** Maps the tolerant result onto the stable Summary card model without Vue, stores or HTTP. */
export function toSummaryInsightItem(insight: InsightResult, now = Date.now()): SummaryInsightItem {
  const presentation = outcomePresentation(insight.outcome_category)
  const title =
    [insight.title, insight.label, insight.config_enum].map(presentInsightTitle).find(Boolean) ?? ''
  const platformKey = insight.platform?.trim()
  const platformLabel = insight.platform_label?.trim() || platformKey

  return {
    id: insight.id,
    title,
    outcomeCategory: insight.outcome_category,
    outcomeLabelKey: presentation.labelKey,
    // The Summary teaser shows `planning` as a neutral grey chip (v3 Summary), not the orange
    // accent the Insights page uses for the same outcome.
    outcomeTone: presentation.key === 'planning' ? 'neutral' : presentation.tone,
    isNew: isNewInsight(insight.created_at, now),
    ...(platformKey && platformKey !== 'all' && platformLabel
      ? { platform: { key: platformKey, label: platformLabel } }
      : {}),
    ...(insight.created_at ? { createdAt: insight.created_at } : {}),
    ...(Number.isFinite(insight.impact_score) ? { impactScore: insight.impact_score } : {}),
    ...(insight.impact_format ? { impactFormat: insight.impact_format } : {}),
    ...(presentInsightTitle(insight.impact_metric)
      ? { impactMetric: presentInsightTitle(insight.impact_metric) }
      : {}),
  }
}
